import { z } from "zod";
import { ConstructionBaseSchema } from "../generated/Construction";
import { ConstructionActionBaseSchema } from "../generated/ConstructionAction";
import { ConstructionMaterialConsumptionBaseSchema } from "../generated/ConstructionMaterialConsumption";
import { ConstructionPlanBaseSchema } from "../generated/ConstructionPlan";
import { ConstructionRecordBaseSchema } from "../generated/ConstructionRecord";
import { GroupBaseSchema } from "../generated/Group";
import { getUserSchema } from "./user";
import { MaterialBaseSchema } from "../generated/Material";
import { WorkBaseSchema } from "../generated/Work";
import { ProcessExecutionBaseSchema } from "../generated/ProcessExecution";
import { QualityRecordBaseSchema } from "../generated/QualityRecord";
import { StirBatchBaseSchema } from "../generated/StirBatch";
import { ConstructionRecordWithUserSchema } from "./construction_records";

export const constructionRowSchema = ConstructionBaseSchema.base.extend({
  ConstructionPlan: ConstructionPlanBaseSchema.base.extend({
    User: getUserSchema.strip(),
    // 工事は Work(施工計画の work_id で解決)。Work は担当者を持たないため User ネストは無い。
    Work: WorkBaseSchema.base.nullable()
  }).nullable(),
  User: getUserSchema.strip(),
  Materials: MaterialBaseSchema.rows,
  ConstructionRecords: ConstructionRecordWithUserSchema.array(),
});
export const constructionRowsSchema = z.array(constructionRowSchema);
export type constructionRow = z.infer<typeof constructionRowSchema>;
export type constructionRows = z.infer<typeof constructionRowsSchema>;

// 施工ツリー(get-construction-tree-from-id)のネスト形。復元は util/hydrators/constructionHydrator が行う。
export const constructionTreeRowSchema = ConstructionBaseSchema.base.extend({
  ConstructionActions: z.array(ConstructionActionBaseSchema.base),
  ConstructionMaterialConsumptions: z.array(ConstructionMaterialConsumptionBaseSchema.base),
  ProcessExecutions: z.array(ProcessExecutionBaseSchema.base),
  QualityRecords: z.array(QualityRecordBaseSchema.base),
  StirBatches: z.array(StirBatchBaseSchema.base),
});
export type constructionTreeRow = z.infer<typeof constructionTreeRowSchema>;

// 施工の正規化応答(重複排除)。従来の ConstructionPlan(Work/User込み)・
// ConstructionRecords(User込み)・Materials(消費記録から解決)・User ネストを related バケットへ分離し、
// ネスト形(constructionRow)への復元は web 側 util/hydrators/constructionHydrator が行う。
// キー名は IndexedDB の SetCache 収集キー(PascalCase 複数形)に合わせてある。
// related.ConstructionPlans / Works / ConstructionRecords には従来 include の
// where(deleted_at: null)を踏襲して alive の行のみ入る(削除済みの参照先は従来どおりネスト null 相当)。
// Materials は従来の attachConsumedMaterials を分解し、alive の消費行(ConstructionMaterialConsumptions)と
// 参照先 Materials を生のまま返す(施工×材料QRの重複排除は web 側ハイドレータが従来と同じ規則で行う)。
// 単一取得(get-construction-from-id)も同形(Constructions 配列に1件)で返し、応答組み立てを共有する。
export const normalizedConstructionResponseSchema = z.object({
  Constructions: ConstructionBaseSchema.base.array(),
  related: z.object({
    ConstructionPlans: ConstructionPlanBaseSchema.base.array(),
    Works: WorkBaseSchema.base.array(),
    ConstructionRecords: ConstructionRecordBaseSchema.base.array(),
    ConstructionMaterialConsumptions: ConstructionMaterialConsumptionBaseSchema.base.array(),
    Materials: MaterialBaseSchema.base.array(),
    // User 内の Group ネストも一般規則どおり分離する(Users は group_id 参照のみ)
    Users: getUserSchema.omit({ Group: true }).strip().array(),
    Groups: GroupBaseSchema.base.array(),
  }),
});
export type NormalizedConstructionResponse = z.infer<typeof normalizedConstructionResponseSchema>;

// 施工ツリーの正規化応答。従来の5兄弟 hasMany(alive のみ)ネストをそのまま related バケットへ分離する。
// ネスト形(constructionTreeRow)への復元は web 側 util/hydrators/constructionHydrator の hydrateConstructionTrees が行う。
export const normalizedConstructionTreeResponseSchema = z.object({
  Constructions: ConstructionBaseSchema.base.array(),
  related: z.object({
    ProcessExecutions: ProcessExecutionBaseSchema.base.array(),
    StirBatches: StirBatchBaseSchema.base.array(),
    ConstructionActions: ConstructionActionBaseSchema.base.array(),
    QualityRecords: QualityRecordBaseSchema.base.array(),
    ConstructionMaterialConsumptions: ConstructionMaterialConsumptionBaseSchema.base.array(),
  }),
});
export type NormalizedConstructionTreeResponse = z.infer<typeof normalizedConstructionTreeResponseSchema>;